import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Users, X, AlertTriangle, RefreshCcw, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { PayrollProfilTab } from "@/pages/admin/PayrollProfilTab";
import { PegawaiHistoryTab } from "@/pages/admin/PegawaiHistoryTab";
import { ContractHistoryTab } from "@/pages/admin/ContractHistoryTab";
import { ExportPegawaiDialog } from "@/pages/admin/ExportPegawaiDialog";

const ROLES = [
  { value: "staff", label: "Staff" },
  { value: "asisten_manager", label: "Asisten Manager" },
  { value: "manager", label: "Manager" },
  { value: "kepala_divisi", label: "Kepala Divisi" },
  { value: "direksi", label: "Direksi" },
  { value: "payroll_admin", label: "Payroll Admin" },
  { value: "admin", label: "Admin" },
];

// Role berhak-tinggi — hanya Admin yang boleh menetapkannya.
const ROLES_PRIVILEGED = new Set(["admin", "payroll_admin"]);
// Role yang boleh melihat tab Riwayat Perubahan.
const ROLES_SEE_HISTORY = new Set(["admin", "kepala_divisi", "direksi"]);
// Field yang perubahannya memerlukan konfirmasi alasan (audit-trail).
const AUDIT_FIELDS = [
  "role",
  "unit_id",
  "lokasi_id",
  "jabatan_id",
  "status_kepegawaian_id",
  "bidang_kerja_id",
  "jabatan_struktural_id",
  "kode_pangkat_id",
  "tingkat_jabatan_id",
  "tanggal_pengangkatan",
  "awal_kontrak",
  "akhir_kontrak",
];

const NONE = "__NONE__";

function empty() {
  return {
    name: "", email: "", npp: "", role: "staff", password: "",
    unit_id: NONE, lokasi_id: NONE, jadwal_id: NONE, jabatan_id: NONE,
    atasan1_id: NONE, atasan2_id: NONE, atasan3_id: NONE,
    wfa_permanen: false, is_active: true,
    kuota_cuti_tahunan: "",
    awal_bergabung: "",
    cost_center_id: NONE,
    koperasi_id: NONE,
    // Field kepegawaian (Feb 2026)
    status_kepegawaian_id: NONE,
    bidang_kerja_id: NONE,
    jabatan_struktural_id: NONE,
    kode_pangkat_id: NONE,
    tingkat_jabatan_id: NONE,
    tanggal_pengangkatan: "",
    awal_kontrak: "",
    akhir_kontrak: "",
    // Field BPJS (Feb 2026)
    nomor_bpjs_ketenagakerjaan: "",
    nomor_bpjs_kesehatan: "",
  };
}

// Hitung sisa hari sampai akhir kontrak (null bila kosong/invalid).
function sisaHariKontrak(dateStr) {
  if (!dateStr) return null;
  try {
    const target = new Date(dateStr + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffMs = target.getTime() - today.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function AdminPegawai() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canSeeHistory = ROLES_SEE_HISTORY.has(user?.role);
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [lokasi, setLokasi] = useState([]);
  const [jadwal, setJadwal] = useState([]);
  const [jabatan, setJabatan] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [koperasiList, setKoperasiList] = useState([]);
  // Master kepegawaian
  const [statusKepegawaian, setStatusKepegawaian] = useState([]);
  const [bidangKerja, setBidangKerja] = useState([]);
  const [jabatanStruktural, setJabatanStruktural] = useState([]);
  const [kodePangkat, setKodePangkat] = useState([]);
  const [tingkatJabatan, setTingkatJabatan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("data");
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [tanggalBerlaku, setTanggalBerlaku] = useState("");
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pendingDiffFields, setPendingDiffFields] = useState([]);
  // Dialog Renewal Kontrak
  const [renewalOpen, setRenewalOpen] = useState(false);
  const [renewalForm, setRenewalForm] = useState({ awal_kontrak: "", akhir_kontrak: "", keterangan: "" });
  const [renewalSaving, setRenewalSaving] = useState(false);
  // Dialog export Excel Pegawai
  const [exportOpen, setExportOpen] = useState(false);

  const nameMap = useMemo(() => {
    const m = {};
    [...units, ...lokasi, ...jadwal, ...jabatan].forEach((x) => (m[x.id] = x.nama));
    return m;
  }, [units, lokasi, jadwal, jabatan]);

  async function loadAll() {
    setLoading(true);
    try {
      const [p, u, l, j, jb, cc, kop, sk, bk, js, kp, tj] = await Promise.all([
        api.get("/pegawai"),
        api.get("/master/unit"),
        api.get("/master/lokasi"),
        api.get("/master/jadwal"),
        api.get("/master/jabatan"),
        api.get("/master/cost-center"),
        api.get("/master/koperasi"),
        api.get("/master/kepegawaian/status-kepegawaian"),
        api.get("/master/kepegawaian/bidang-kerja"),
        api.get("/master/kepegawaian/jabatan-struktural"),
        api.get("/master/kepegawaian/kode-pangkat"),
        api.get("/master/kepegawaian/tingkat-jabatan"),
      ]);
      setRows(p.data);
      setUnits(u.data);
      setLokasi(l.data);
      setJadwal(j.data);
      setJabatan(jb.data);
      setCostCenters(cc.data);
      setKoperasiList(kop.data);
      setStatusKepegawaian(sk.data);
      setBidangKerja(bk.data);
      setJabatanStruktural(js.data);
      setKodePangkat(kp.data);
      setTingkatJabatan(tj.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); }, []);

  function openCreate() {
    setEditing(null);
    setForm(empty());
    setTab("data");
    setOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setTab("data");
    setForm({
      name: row.name || "",
      email: row.email || "",
      npp: row.npp || "",
      role: row.role || "staff",
      password: "",
      unit_id: row.unit_id || NONE,
      lokasi_id: row.lokasi_id || NONE,
      jadwal_id: row.jadwal_id || NONE,
      jabatan_id: row.jabatan_id || NONE,
      atasan1_id: row.atasan1_id || NONE,
      atasan2_id: row.atasan2_id || NONE,
      atasan3_id: row.atasan3_id || NONE,
      wfa_permanen: !!row.wfa_permanen,
      is_active: row.is_active !== false,
      kuota_cuti_tahunan: row.kuota_cuti_tahunan != null ? String(row.kuota_cuti_tahunan) : "",
      awal_bergabung: row.awal_bergabung || "",
      cost_center_id: row.cost_center_id || NONE,
      koperasi_id: row.koperasi_id || NONE,
      status_kepegawaian_id: row.status_kepegawaian_id || NONE,
      bidang_kerja_id: row.bidang_kerja_id || NONE,
      jabatan_struktural_id: row.jabatan_struktural_id || NONE,
      kode_pangkat_id: row.kode_pangkat_id || NONE,
      tingkat_jabatan_id: row.tingkat_jabatan_id || NONE,
      tanggal_pengangkatan: row.tanggal_pengangkatan || "",
      awal_kontrak: row.awal_kontrak || "",
      akhir_kontrak: row.akhir_kontrak || "",
      // Field BPJS
      nomor_bpjs_ketenagakerjaan: row.nomor_bpjs_ketenagakerjaan || "",
      nomor_bpjs_kesehatan: row.nomor_bpjs_kesehatan || "",
    });
    setOpen(true);
  }

  function buildPayload() {
    const payload = { ...form };
    [
      "unit_id", "lokasi_id", "jadwal_id", "jabatan_id",
      "atasan1_id", "atasan2_id", "atasan3_id",
      "cost_center_id", "koperasi_id",
      "status_kepegawaian_id", "bidang_kerja_id",
      "jabatan_struktural_id", "kode_pangkat_id", "tingkat_jabatan_id",
    ].forEach((k) => { if (payload[k] === NONE) payload[k] = null; });
    if (payload.kuota_cuti_tahunan === "" || payload.kuota_cuti_tahunan == null) {
      payload.kuota_cuti_tahunan = null;
    } else {
      const n = parseInt(payload.kuota_cuti_tahunan, 10);
      if (isNaN(n) || n <= 0 || n > 60) {
        toast.error("Kuota Cuti harus 1-60 hari, atau kosongkan untuk default");
        return null;
      }
      payload.kuota_cuti_tahunan = n;
    }
    ["awal_bergabung", "tanggal_pengangkatan", "awal_kontrak", "akhir_kontrak"].forEach((k) => {
      if (!payload[k]) payload[k] = null;
    });
    return payload;
  }

  // Deteksi field audit-trail yang berubah saat edit.
  function diffAuditFields(payload) {
    if (!editing) return [];
    const diff = [];
    for (const fld of AUDIT_FIELDS) {
      const oldV = editing[fld] || null;
      const newV = payload[fld] || null;
      if (oldV !== newV) diff.push(fld);
    }
    return diff;
  }

  async function save() {
    const payload = buildPayload();
    if (!payload) return;
    if (!editing) {
      if (!payload.password || payload.password.length < 6) {
        toast.error("Password minimal 6 karakter");
        return;
      }
      await persist(payload);
      return;
    }
    if (!payload.password) delete payload.password;
    // Edit: jika ada field audit yang berubah → minta Alasan terlebih dahulu.
    const diff = diffAuditFields(payload);
    if (diff.length > 0) {
      setPendingPayload(payload);
      setPendingDiffFields(diff);
      setReason("");
      setTanggalBerlaku(todayIso());
      setReasonOpen(true);
      return;
    }
    await persist(payload);
  }

  async function persist(payload) {
    setSaving(true);
    try {
      if (!editing) {
        await api.post("/pegawai", payload);
        toast.success("Pegawai berhasil ditambahkan");
      } else {
        await api.put(`/pegawai/${editing.id}`, payload);
        toast.success("Pegawai berhasil diperbarui");
      }
      setOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function confirmWithReason() {
    const p = { ...(pendingPayload || {}) };
    const r = (reason || "").trim();
    const tgl = (tanggalBerlaku || "").trim();
    if (!tgl) {
      toast.error("Tanggal Berlaku wajib diisi");
      return;
    }
    if (r) p.change_reason = r;
    p.tanggal_berlaku = tgl;
    setReasonOpen(false);
    await persist(p);
  }

  // ----- Perpanjangan Kontrak (Renewal) -----
  function openRenewal() {
    // Default awal kontrak baru = hari ini (atau akhir+1 bila belum lewat).
    const today = todayIso();
    let defaultAwal = today;
    if (editing?.akhir_kontrak) {
      const akhir = new Date(editing.akhir_kontrak + "T00:00:00");
      if (!isNaN(akhir.getTime()) && akhir.getTime() >= Date.now()) {
        const next = new Date(akhir.getTime() + 24 * 60 * 60 * 1000);
        defaultAwal = next.toISOString().slice(0, 10);
      }
    }
    // Default akhir kontrak = 1 tahun setelah awal.
    const d = new Date(defaultAwal + "T00:00:00");
    d.setFullYear(d.getFullYear() + 1);
    d.setDate(d.getDate() - 1);
    const defaultAkhir = d.toISOString().slice(0, 10);
    setRenewalForm({ awal_kontrak: defaultAwal, akhir_kontrak: defaultAkhir, keterangan: "" });
    setRenewalOpen(true);
  }

  async function saveRenewal() {
    if (!editing) return;
    const { awal_kontrak, akhir_kontrak, keterangan } = renewalForm;
    if (!awal_kontrak || !akhir_kontrak) {
      toast.error("Awal & Akhir Kontrak wajib diisi");
      return;
    }
    if (akhir_kontrak <= awal_kontrak) {
      toast.error("Akhir Kontrak harus setelah Awal Kontrak");
      return;
    }
    setRenewalSaving(true);
    try {
      await api.post(`/pegawai/${editing.id}/renewal`, {
        awal_kontrak,
        akhir_kontrak,
        keterangan: (keterangan || "").trim() || null,
      });
      toast.success("Kontrak berhasil diperpanjang & komponen upah diaktifkan kembali");
      setRenewalOpen(false);
      // Reload data + sinkron state editing agar tombol & tab langsung mencerminkan periode baru.
      await loadAll();
      // Update editing in-place from fresh rows:
      // AdminPegawai menyimpan `editing` → update agar sisaHari re-calculate.
      setEditing((prev) => prev ? { ...prev, awal_kontrak, akhir_kontrak } : prev);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setRenewalSaving(false);
    }
  }

  async function del(row) {
    if (!confirm(`Hapus pegawai ${row.name}?`)) return;
    try {
      await api.delete(`/pegawai/${row.id}`);
      toast.success("Pegawai dihapus");
      await loadAll();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  // Pegawai dengan kontrak akan habis ≤ 30 hari (hanya admin).
  const kontrakSoonList = useMemo(() => {
    if (!isAdmin) return [];
    return rows
      .map((r) => ({ r, sisa: sisaHariKontrak(r.akhir_kontrak) }))
      .filter((x) => x.sisa !== null && x.sisa <= 30 && r2Active(x.r));
  }, [rows, isAdmin]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-pegawai">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Master Pegawai
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Kelola akun pegawai, role, lokasi &amp; jadwal kerja.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Button
              onClick={() => setExportOpen(true)}
              variant="outline"
              className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="pegawai-export-xlsx-btn"
            >
              <FileSpreadsheet size={16} /> Export Excel
            </Button>
          )}
          <Button onClick={openCreate} className="gap-2 bg-teal-700 hover:bg-teal-800" data-testid="pegawai-add-btn">
            <Plus size={16} /> Tambah Pegawai
          </Button>
        </div>
      </div>

      <ExportPegawaiDialog open={exportOpen} onOpenChange={setExportOpen} />

      {isAdmin && kontrakSoonList.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="pegawai-kontrak-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-amber-900 text-sm">
                {kontrakSoonList.length} pegawai: kontrak akan habis ≤ 30 hari
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {kontrakSoonList.slice(0, 20).map(({ r, sisa }) => (
                  <span
                    key={r.id}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border ${
                      sisa < 0 ? "bg-rose-100 text-rose-900 border-rose-200"
                      : sisa <= 7 ? "bg-rose-50 text-rose-800 border-rose-200"
                      : "bg-amber-100 text-amber-900 border-amber-200"
                    }`}
                    data-testid={`pegawai-kontrak-badge-${r.id}`}
                  >
                    {r.name} · {sisa < 0 ? `lewat ${Math.abs(sisa)}h` : `${sisa}h lagi`}
                  </span>
                ))}
                {kontrakSoonList.length > 20 ? (
                  <span className="text-[11px] text-amber-800 italic">+ {kontrakSoonList.length - 20} lainnya…</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">NPP</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nama</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Role</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Unit</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Lokasi</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Jadwal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Kontrak</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">WFA</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <Users size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada pegawai</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => {
                  const sisa = sisaHariKontrak(r.akhir_kontrak);
                  const warn = isAdmin && sisa !== null && sisa <= 30;
                  return (
                  <TableRow key={r.id} data-testid={`pegawai-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell className="font-mono-clock text-xs text-slate-700">{r.npp}</TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {r.name}
                        {warn ? (
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                              sisa < 0 ? "bg-rose-100 text-rose-900 border-rose-200"
                              : sisa <= 7 ? "bg-rose-50 text-rose-800 border-rose-200"
                              : "bg-amber-50 text-amber-800 border-amber-200"
                            }`}
                            data-testid={`pegawai-kontrak-chip-${r.id}`}
                            title={`Akhir kontrak: ${r.akhir_kontrak}`}
                          >
                            <AlertTriangle size={10} />
                            {sisa < 0 ? "Lewat" : "Akan Habis"}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-slate-500">{r.email}</div>
                    </TableCell>
                    <TableCell>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-teal-50 text-teal-800 border border-teal-100">
                        {r.role?.replace("_", " ")}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-700 text-sm">{nameMap[r.unit_id] || "—"}</TableCell>
                    <TableCell className="text-slate-700 text-sm">{nameMap[r.lokasi_id] || "—"}</TableCell>
                    <TableCell className="text-slate-700 text-sm">{nameMap[r.jadwal_id] || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                      {r.akhir_kontrak ? r.akhir_kontrak : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell>
                      {r.wfa_permanen ? (
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100">Ya</span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => openEdit(r)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900 mr-3"
                        data-testid={`pegawai-edit-${r.id}`}
                      >
                        <Pencil size={13} /> Edit
                      </button>
                      {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pegawai-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pegawai" : "Tambah Pegawai"}</DialogTitle>
            <DialogDescription>
              {editing ? "Perbarui data & penugasan pegawai." : "Lengkapi data pegawai baru & tetapkan lokasi/jadwal."}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="pegawai-tabs">
              <TabsTrigger value="data" data-testid="tab-pegawai-data">Data Pegawai</TabsTrigger>
              <TabsTrigger value="kepegawaian" data-testid="tab-pegawai-kepegawaian">Kepegawaian & Kontrak</TabsTrigger>
              <TabsTrigger value="payroll" data-testid="tab-pegawai-payroll">Payroll</TabsTrigger>
              {editing && canSeeHistory ? (
                <>
                  <TabsTrigger value="kontrak" data-testid="tab-pegawai-kontrak">Riwayat Kontrak</TabsTrigger>
                  <TabsTrigger value="history" data-testid="tab-pegawai-history">Riwayat Perubahan</TabsTrigger>
                </>
              ) : null}
            </TabsList>

            <TabsContent value="data" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nama Lengkap" testId="f-name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-name" />
            </Field>
            <Field label="NPP (Employee ID)" testId="f-npp">
              <Input value={form.npp} onChange={(e) => setForm({ ...form, npp: e.target.value })} data-testid="input-npp" />
            </Field>
            <Field label="Email" testId="f-email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
            </Field>
            <Field label={editing ? "Password (kosongkan jika tidak diubah)" : "Password"} testId="f-password">
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="input-password" />
            </Field>
            <Field label="Role" testId="f-role">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="input-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.filter((r) => isAdmin || !ROLES_PRIVILEGED.has(r.value)).map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Jabatan (legacy)" testId="f-jabatan">
              <Select value={form.jabatan_id || NONE} onValueChange={(v) => setForm({ ...form, jabatan_id: v })}>
                <SelectTrigger data-testid="input-jabatan"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {jabatan.map((x) => (<SelectItem key={x.id} value={x.id}>{x.nama}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Unit Kerja" testId="f-unit">
              <Select value={form.unit_id || NONE} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                <SelectTrigger data-testid="input-unit"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {units.map((x) => (<SelectItem key={x.id} value={x.id}>{x.nama}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Lokasi Kerja" testId="f-lokasi">
              <Select value={form.lokasi_id || NONE} onValueChange={(v) => setForm({ ...form, lokasi_id: v })}>
                <SelectTrigger data-testid="input-lokasi"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {lokasi.map((x) => (<SelectItem key={x.id} value={x.id}>{x.nama}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Jadwal Kerja" testId="f-jadwal">
              <Select value={form.jadwal_id || NONE} onValueChange={(v) => setForm({ ...form, jadwal_id: v })}>
                <SelectTrigger data-testid="input-jadwal"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {jadwal.map((x) => (<SelectItem key={x.id} value={x.id}>{x.nama}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2 mt-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-teal-700 font-bold">
                Alur Persetujuan (Cuti &amp; Izin)
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Level kosong akan otomatis dilewati. Tanpa atasan sama sekali = pengajuan otomatis disetujui.
              </p>
            </div>
            {[1, 2, 3].map((lvl) => {
              const key = `atasan${lvl}_id`;
              return (
                <Field key={key} label={lvl === 1 ? "Atasan Level 1 (Langsung)" : `Atasan Level ${lvl}`} testId={`f-atasan${lvl}`}>
                  <Select value={form[key] || NONE} onValueChange={(v) => setForm({ ...form, [key]: v })}>
                    <SelectTrigger data-testid={`input-atasan${lvl}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— (dilewati)</SelectItem>
                      {rows
                        .filter((p) => (!editing || p.id !== editing.id) && p.role !== "staff")
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} — {p.role?.replace("_", " ")}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </Field>
              );
            })}
            <div className="sm:col-span-2 flex items-center gap-6 mt-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={form.wfa_permanen} onCheckedChange={(v) => setForm({ ...form, wfa_permanen: v })} data-testid="input-wfa" />
                WFA Permanen (lewati validasi radius)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="input-active" />
                Aktif
              </label>
            </div>
            <Field label="Kuota Cuti Tahunan (opsional — kosongkan untuk pakai default global)" testId="f-kuota-cuti">
              <Input
                type="number"
                min="1"
                max="60"
                placeholder="Kosongkan untuk pakai default"
                value={form.kuota_cuti_tahunan}
                onChange={(e) => setForm({ ...form, kuota_cuti_tahunan: e.target.value })}
                data-testid="input-kuota-cuti"
              />
            </Field>
            <Field label="Awal Bergabung (tanggal anniversary — reset kuota H+1 tiap tahun)" testId="f-awal-bergabung">
              <Input
                type="date"
                value={form.awal_bergabung}
                onChange={(e) => setForm({ ...form, awal_bergabung: e.target.value })}
                data-testid="input-awal-bergabung"
              />
            </Field>
            <Field label="Cost Center (Pembebanan Biaya)" testId="f-cost-center">
              <Select
                value={form.cost_center_id}
                onValueChange={(v) => setForm({ ...form, cost_center_id: v })}
              >
                <SelectTrigger data-testid="input-cost-center">
                  <SelectValue placeholder="Pilih Cost Center" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Tidak diatur —</SelectItem>
                  {costCenters.filter((c) => c.is_active !== false || c.id === form.cost_center_id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.kode ? `[${c.kode}] ` : ""}{c.nama}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Kelola daftar di menu <b>Cost Center</b>.
              </p>
            </Field>
            <Field label="Koperasi" testId="f-koperasi">
              <Select
                value={form.koperasi_id}
                onValueChange={(v) => setForm({ ...form, koperasi_id: v })}
              >
                <SelectTrigger data-testid="input-koperasi">
                  <SelectValue placeholder="Pilih Koperasi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Tidak diatur —</SelectItem>
                  {koperasiList.filter((c) => c.is_active !== false || c.id === form.koperasi_id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.kode ? `[${c.kode}] ` : ""}{c.nama}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Kelola daftar di menu <b>Koperasi</b>.
              </p>
            </Field>
          </div>
            </TabsContent>

            <TabsContent value="kepegawaian" className="mt-4">
              {editing && (() => {
                const sisa = sisaHariKontrak(form.akhir_kontrak);
                const bisaPerpanjang = sisa !== null && sisa <= 30;
                if (!bisaPerpanjang) return null;
                return (
                  <div
                    className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
                    data-testid="renewal-cta"
                  >
                    <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-amber-900 text-sm">
                        Akhir Kontrak {sisa < 0 ? `sudah lewat ${Math.abs(sisa)} hari` : sisa === 0 ? "berakhir hari ini" : `tinggal ${sisa} hari`}
                      </div>
                      <div className="text-[11px] text-amber-800 mt-0.5">
                        Perpanjang kontrak untuk mengarsipkan periode lama, memasang periode baru, dan mengaktifkan kembali seluruh komponen upah.
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={openRenewal}
                      className="gap-1.5 bg-teal-700 hover:bg-teal-800 shrink-0"
                      data-testid="renewal-open-btn"
                    >
                      <RefreshCcw size={14} /> Perpanjang Kontrak
                    </Button>
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MasterDropdown
                  label="Status Kepegawaian" testId="f-status-kepegawaian"
                  value={form.status_kepegawaian_id} items={statusKepegawaian}
                  onChange={(v) => setForm({ ...form, status_kepegawaian_id: v })}
                />
                <MasterDropdown
                  label="Bidang Kerja" testId="f-bidang-kerja"
                  value={form.bidang_kerja_id} items={bidangKerja}
                  onChange={(v) => setForm({ ...form, bidang_kerja_id: v })}
                />
                <MasterDropdown
                  label="Jabatan" testId="f-jabatan-struktural"
                  value={form.jabatan_struktural_id} items={jabatanStruktural}
                  onChange={(v) => setForm({ ...form, jabatan_struktural_id: v })}
                />
                <MasterDropdown
                  label="Kode Pangkat" testId="f-kode-pangkat"
                  value={form.kode_pangkat_id} items={kodePangkat}
                  onChange={(v) => setForm({ ...form, kode_pangkat_id: v })}
                />
                <MasterDropdown
                  label="Tingkat Jabatan" testId="f-tingkat-jabatan"
                  value={form.tingkat_jabatan_id} items={tingkatJabatan}
                  onChange={(v) => setForm({ ...form, tingkat_jabatan_id: v })}
                />
                <Field label="Tanggal Pengangkatan PT / Pegawai Tetap (opsional)" testId="f-tanggal-pengangkatan">
                  <Input
                    type="date"
                    value={form.tanggal_pengangkatan}
                    onChange={(e) => setForm({ ...form, tanggal_pengangkatan: e.target.value })}
                    data-testid="input-tanggal-pengangkatan"
                  />
                </Field>
                <Field label="Awal Kontrak (opsional)" testId="f-awal-kontrak">
                  <Input
                    type="date"
                    value={form.awal_kontrak}
                    onChange={(e) => setForm({ ...form, awal_kontrak: e.target.value })}
                    data-testid="input-awal-kontrak"
                  />
                </Field>
                <Field label="Akhir Kontrak (opsional)" testId="f-akhir-kontrak">
                  <Input
                    type="date"
                    value={form.akhir_kontrak}
                    onChange={(e) => setForm({ ...form, akhir_kontrak: e.target.value })}
                    data-testid="input-akhir-kontrak"
                  />
                  {form.akhir_kontrak ? (() => {
                    const s = sisaHariKontrak(form.akhir_kontrak);
                    if (s === null) return null;
                    const style = s < 0 ? "text-rose-700" : s <= 7 ? "text-rose-700" : s <= 30 ? "text-amber-700" : "text-slate-500";
                    return (
                      <p className={`text-[11px] mt-1 font-semibold ${style}`} data-testid="akhir-kontrak-info">
                        {s < 0 ? `Sudah lewat ${Math.abs(s)} hari` : s === 0 ? "Berakhir hari ini" : `${s} hari lagi`}
                      </p>
                    );
                  })() : null}
                </Field>
                <Field label="No. BPJS Ketenagakerjaan (opsional)" testId="f-bpjs-ketenagakerjaan">
                  <Input
                    value={form.nomor_bpjs_ketenagakerjaan}
                    onChange={(e) => setForm({ ...form, nomor_bpjs_ketenagakerjaan: e.target.value })}
                    placeholder="Contoh: 13044595570"
                    data-testid="input-bpjs-ketenagakerjaan"
                  />
                </Field>
                <Field label="No. BPJS Kesehatan (opsional)" testId="f-bpjs-kesehatan">
                  <Input
                    value={form.nomor_bpjs_kesehatan}
                    onChange={(e) => setForm({ ...form, nomor_bpjs_kesehatan: e.target.value })}
                    placeholder="Contoh: 0000368956236"
                    data-testid="input-bpjs-kesehatan"
                  />
                </Field>
              </div>
              <p className="text-[11px] text-slate-500 mt-4">
                Daftar pilihan dikelola di <b>Konfigurasi Kebijakan → Master Data Kepegawaian</b>.
              </p>
            </TabsContent>

            <TabsContent value="payroll" className="mt-4">
              {editing ? (
                <PayrollProfilTab userId={editing.id} npp={editing.npp} />
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900" data-testid="payroll-need-save">
                  Simpan data pegawai terlebih dahulu, lalu buka kembali menu <b>Edit</b> untuk mengisi profil Payroll
                  (rekening, NPWP, gaji pokok, dan komponen gaji aktif).
                </div>
              )}
            </TabsContent>

            {editing && canSeeHistory ? (
              <>
                <TabsContent value="kontrak" className="mt-4">
                  <ContractHistoryTab userId={editing.id} />
                </TabsContent>
                <TabsContent value="history" className="mt-4">
                  <PegawaiHistoryTab userId={editing.id} />
                </TabsContent>
              </>
            ) : null}
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="pegawai-cancel-btn">Batal</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="pegawai-save-btn">
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Konfirmasi Alasan Perubahan — muncul bila ada field audit yang berubah. */}
      <Dialog open={reasonOpen} onOpenChange={setReasonOpen}>
        <DialogContent data-testid="pegawai-reason-dialog">
          <DialogHeader>
            <DialogTitle>Konfirmasi Perubahan</DialogTitle>
            <DialogDescription>
              Perubahan field berikut akan dicatat ke Riwayat Perubahan.
              Isi <b>Tanggal Berlaku</b> dan tuliskan alasan singkat bila perlu.
            </DialogDescription>
          </DialogHeader>
          {pendingDiffFields.length ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              <div className="font-bold uppercase tracking-wider text-slate-500 mb-1">Field yang berubah</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {pendingDiffFields.map((f) => (
                  <li key={f}>{humanizeField(f)}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Tanggal Berlaku <span className="text-rose-600">*</span>
            </Label>
            <Input
              type="date"
              value={tanggalBerlaku}
              onChange={(e) => setTanggalBerlaku(e.target.value)}
              className="mt-1.5"
              data-testid="pegawai-reason-tanggal-berlaku"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Default: hari ini. Anda boleh memundurkan (backdate) ke tanggal lampau — ini akan tersimpan di Riwayat Perubahan terpisah dari tanggal pencatatan sistem.
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Alasan Perubahan (Opsional)
            </Label>
            <Textarea
              placeholder="Contoh: Promosi jabatan sesuai SK No. 123/2026"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={400}
              className="mt-1.5"
              data-testid="pegawai-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonOpen(false)} data-testid="pegawai-reason-cancel">
              Batal
            </Button>
            <Button
              onClick={confirmWithReason}
              disabled={saving}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="pegawai-reason-confirm"
            >
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Renewal Kontrak */}
      <Dialog open={renewalOpen} onOpenChange={setRenewalOpen}>
        <DialogContent data-testid="pegawai-renewal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCcw size={18} className="text-teal-700" />
              Perpanjang Kontrak
            </DialogTitle>
            <DialogDescription>
              Periode kontrak lama akan diarsipkan ke <b>Riwayat Kontrak</b> dan seluruh komponen upah
              pegawai akan diaktifkan kembali untuk periode baru.
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              <div className="font-bold uppercase tracking-wider text-slate-500 mb-1">Periode Lama (akan diarsipkan)</div>
              <div className="font-mono-clock">
                {editing.awal_kontrak || "—"} s.d. {editing.akhir_kontrak || "—"}
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Awal Kontrak Baru <span className="text-rose-600">*</span>
              </Label>
              <Input
                type="date"
                className="mt-1.5"
                value={renewalForm.awal_kontrak}
                onChange={(e) => setRenewalForm({ ...renewalForm, awal_kontrak: e.target.value })}
                data-testid="renewal-input-awal"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Akhir Kontrak Baru <span className="text-rose-600">*</span>
              </Label>
              <Input
                type="date"
                className="mt-1.5"
                value={renewalForm.akhir_kontrak}
                onChange={(e) => setRenewalForm({ ...renewalForm, akhir_kontrak: e.target.value })}
                data-testid="renewal-input-akhir"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Keterangan (Opsional)
            </Label>
            <Textarea
              className="mt-1.5"
              value={renewalForm.keterangan}
              onChange={(e) => setRenewalForm({ ...renewalForm, keterangan: e.target.value })}
              placeholder="Contoh: Perpanjangan 1 tahun · SK No. 456/2026"
              rows={3}
              maxLength={400}
              data-testid="renewal-input-keterangan"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewalOpen(false)} data-testid="renewal-cancel-btn">
              Batal
            </Button>
            <Button
              onClick={saveRenewal}
              disabled={renewalSaving}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="renewal-save-btn"
            >
              {renewalSaving ? "Menyimpan…" : "Simpan Perpanjangan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function MasterDropdown({ label, testId, value, items, onChange }) {
  return (
    <Field label={label} testId={testId}>
      <Select value={value || NONE} onValueChange={onChange}>
        <SelectTrigger data-testid={`input-${testId}`}>
          <SelectValue placeholder="— Tidak diatur —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— Tidak diatur —</SelectItem>
          {items.filter((x) => x.is_active !== false || x.id === value).map((x) => (
            <SelectItem key={x.id} value={x.id}>
              {x.kode ? `[${x.kode}] ` : ""}{x.nama}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

function r2Active(r) {
  return r?.is_active !== false;
}

function humanizeField(f) {
  const map = {
    role: "Role Sistem",
    unit_id: "Unit Kerja",
    lokasi_id: "Lokasi Kerja",
    jabatan_id: "Jabatan (Legacy)",
    status_kepegawaian_id: "Status Kepegawaian",
    bidang_kerja_id: "Bidang Kerja",
    jabatan_struktural_id: "Jabatan",
    kode_pangkat_id: "Kode Pangkat",
    tingkat_jabatan_id: "Tingkat Jabatan",
    tanggal_pengangkatan: "Tanggal Pengangkatan",
    awal_kontrak: "Awal Kontrak",
    akhir_kontrak: "Akhir Kontrak",
  };
  return map[f] || f;
}

